import { gql, useMutation } from '@apollo/client';
import {
    CircularProgress,
    IconButton,
    Chip as MuiChip,
    Popper,
    PopperProps,
    Stack,
    styled,
    useTheme,
    TextField
} from '@mui/material';
import { isEmpty, snakeCase } from 'lodash';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import * as React from 'react';

import { Autocomplete } from './AutoComplete';
import { NestedSubOption } from './types';
import { filter } from './utils';

export type { NestedOption, NestedSubOption, Props } from './types';

// Dummy mutation for components that don't need to add custom options
const DUMMY_MUTATION = gql`
    mutation DummyMutation {
        __typename
    }
`;

const StyledChip = styled('div', {
    shouldForwardProp: prop => prop !== 'isFixed',
})<{ isFixed?: boolean }>(({ theme, isFixed }) => ({
    backgroundColor: isFixed ? theme.palette.primary.light : theme.palette.common.black,
    color: isFixed ? theme.palette.primary.main : theme.palette.text.primary,
    borderRadius: '16px',
    padding: '4px 8px',
    paddingLeft: isFixed ? '16px' : '8px',
    marginRight: 4,
    display: 'inline-block',
    height: 'auto',
    fontSize: '12px',
    fontWeight: 500,
    border: `1px solid ${isFixed ? theme.palette.primary.main : theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
        fontSize: '12px',
    },
}));

const EscHint = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '4px 8px',
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontSize: '12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

const EscHintIcon = styled('i')(({ theme }) => ({
    marginRight: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
}));

const PopperWithEscHint = (props: PopperProps) => {
    const { children, ...other } = props;
    return (
        <Popper {...other}>
            <div>
                <EscHint>
                    <EscHintIcon className='fi fi-br-info' />
                    Appuyez sur Esc pour fermer la liste.
                </EscHint>
                {children as React.ReactNode}
            </div>
        </Popper>
    );
};

const ViewListContainer = styled('div')(() => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 4,
    display: 'inline-block',
}));

const FixedItemIcon = styled('div')(({ theme }) => ({
    position: 'absolute',
    left: 8,
    top: 4,
    zIndex: 1,
    '& i': {
        color: theme.palette.common.white,
        fontSize: 12,
    },
}));

const ViewList = (data: { options: any; value: any; multiple?: boolean }) => {
    const { options, value, multiple } = data;

    if (!multiple) {
        const item = options?.find(option => option.id === value);
        return item ? (
            <ViewListContainer>
                {item?.isFixed && (
                    <FixedItemIcon>
                        <i className="fi-sr-star" />
                    </FixedItemIcon>
                )}
                <StyledChip isFixed={item?.isFixed}>
                </StyledChip>
            </ViewListContainer>
        ) : null;
    }

    return (
        <div>
            {value?.map((_item, index) => {
                const item = options?.find(option => option.id === _item);
                return (
                    <ViewListContainer key={item?.id}>
                        {item?.isFixed && (
                            <FixedItemIcon>
                                <i className="fi-sr-star" />
                            </FixedItemIcon>
                        )}
                        <StyledChip isFixed={item?.isFixed}>
                            {item?.name}
                        </StyledChip>
                    </ViewListContainer>
                );
            })}
        </div>
    );
};

const TagContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 8,
    display: 'inline-block',
}));

const StyledMuiChip = styled(MuiChip)(({ theme }) => ({
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    fontFamily: 'Lemon milk pro',
    fontWeight: 'normal',
    borderRadius: '8px',
    height: 'auto',
    '& .MuiChip-deleteIcon': {
        fontSize: 14,
        color: theme.palette.primary.main,
    },
}));

const EndAdornmentContainer = styled(Stack)(({ theme }) => ({
    position: 'absolute',
    right: 0,
    top: '8px',
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
}));

export const AutoCompleteField = React.forwardRef<
    HTMLInputElement,
    {
        value: string[] | string;
        onChange: any;
        label: string;
        mutation: any;
        refetchQueries?: any;
        placeholder?: string;
        disabled?: boolean;
        options?: NestedSubOption[];
        groupBy: (option: NestedSubOption) => string | undefined;
        allowAdd?: boolean;
        viewOnly?: boolean;
        multiple?: boolean;
        error?: boolean;
        renderOptionWithIcon?: (props: any, option: NestedSubOption) => React.ReactNode;
        newValueResolver?: (data: any, dialogValue: NestedSubOption, iconValue?: string) => NestedSubOption;
        iconMapper?: (icon: string) => string;
    }
>((props, ref) => {
    const theme = useTheme();
    const autoCompleteRef = React.useRef<any>(null);

    // Use the provided mutation or a dummy one
    const [save, { loading }] = useMutation(props.mutation || DUMMY_MUTATION, {
        refetchQueries: props.refetchQueries,
        awaitRefetchQueries: props.refetchQueries?.length > 0,
    });

    const [isOpen, setIsOpen] = React.useState(false);

    const { showSnackbar } = useSnackbar();

    const [search, setSearch] = React.useState('');

    const [options, setOptions] = React.useState<NestedSubOption[]>([]);
    const [customOptions, setCustomOptions] = React.useState<NestedSubOption[]>([]);

    const selectedValues = React.useMemo(() => {
        if (!props.multiple) {
            const singleValue = [...options, ...customOptions]?.find(option => option?.id === props.value);
            return singleValue ? [singleValue] : [];
        }
        return (props.value as string[])?.map(value => {
            return [...options, ...customOptions]?.find(option => option?.id === value);
        });
    }, [props.value, options, customOptions, props.multiple]);

    // Default resolver function
    const defaultResolver = React.useCallback(
        (data: any, dialogValue: NestedSubOption, iconValue?: string): NestedSubOption => {
            return {
                name: dialogValue.name,
                id: data._id,
                icon: iconValue,
                parent: { name: 'Autre', id: data.profession?._id || 'other' },
            };
        },
        [],
    );

    const handleAddCustomOption = React.useCallback(
        async (dialogValue: NestedSubOption) => {
            // If no real mutation is provided, we can't add custom options
            if (!props.mutation) {
                showSnackbar(`Impossible d'ajouter des ${props.label} personnalisés`, 'warning');
                return;
            }

            let data = null;
            try {
                const { data: resultData } = await save({
                    variables: {
                        name: dialogValue.name,
                    },
                });
                data = resultData?.data;
                showSnackbar(`${props.label} ajouté avec succès`, 'success');
            } catch (error) {
                showSnackbar(`Erreur lors de l'ajout du ${props.label}`, 'error');
                return;
            }
            if (!data) return;

            // Check if any existing option has an icon
            const hasIcon = [...options, ...customOptions].some(option => option?.icon);
            const iconValue = hasIcon ? "shape" : undefined;

            // Use custom resolver if provided, otherwise use default
            const resolver = props.newValueResolver || defaultResolver;
            const newOption = resolver(data, dialogValue, iconValue);

            if (!props.multiple) {
                // For single value, replace the current value
                props.onChange(newOption.id);
            } else {
                const newValues: NestedSubOption[] = [
                    ...(selectedValues || []),
                    newOption,
                ];
                // remove inputValue from the list
                props.onChange(newValues?.map(({ id }) => id));
            }

            const newOptions = [
                ...customOptions,
                newOption,
            ];
            setCustomOptions(newOptions);
            setSearch('');
        },
        [props, options, customOptions, defaultResolver, showSnackbar, save, selectedValues],
    );

    React.useEffect(() => {
        setOptions(props.options || []);
    }, [props.options]);

    return (
        <React.Fragment>
            {props.viewOnly && <ViewList options={[...options, ...customOptions]} value={props.value} multiple={props.multiple} />}
            {!props.viewOnly && (
                <Autocomplete
                    ref={autoCompleteRef}
                    multiple={props.multiple !== false}
                    disablePortal
                    PopperComponent={PopperWithEscHint}
                    inputValue={search}
                    value={props.multiple !== false ? selectedValues : (selectedValues[0] || null)}
                    disabled={props.disabled || loading}
                    disableCloseOnSelect={props.multiple !== false}
                    groupBy={props.groupBy}
                    open={isOpen}
                    ListboxProps={{
                        sx: {
                            maxHeight: '300px',
                            overflow: 'auto',
                        },
                    }}
                    onKeyDown={event => {
                        // escape close the dropdown
                        if (event.key === 'Escape') {
                            // clear search
                            setSearch('');
                            setIsOpen(false);
                        }
                        if (event.key === 'Enter') {
                            if (!props.allowAdd) return;
                            // @ts-ignore Prevent's default 'Enter' behavior.
                            event.defaultMuiPrevented = true;
                            handleAddCustomOption({
                                name: search,
                                id: snakeCase(search),
                            });
                        }
                    }}
                    onChange={(event, value: NestedSubOption[] | NestedSubOption | null) => {
                        if (props.multiple !== false) {
                            // Multiple mode
                            const arrayValue = value as NestedSubOption[];
                            const customValue = arrayValue?.find(option => option?.inputValue);
                            if (customValue) {
                                handleAddCustomOption({
                                    name: customValue?.inputValue,
                                    id: snakeCase(customValue?.inputValue),
                                });
                            } else {
                                // Filter out any attempt to remove fixed items
                                const currentSelectedValues = selectedValues || [];
                                const fixedItems = currentSelectedValues.filter(item => item?.isFixed);
                                const newValues = arrayValue || [];

                                // Ensure all fixed items are preserved
                                const finalValues = [...fixedItems];
                                newValues.forEach(item => {
                                    if (!item?.isFixed && !finalValues.find(existing => existing?.id === item?.id)) {
                                        finalValues.push(item);
                                    }
                                });

                                props.onChange(finalValues?.map((item: NestedSubOption) => item?.id)?.filter(Boolean) || []);
                            }
                        } else {
                            // Single mode
                            const singleValue = value as NestedSubOption;
                            if (singleValue?.inputValue) {
                                handleAddCustomOption({
                                    name: singleValue?.inputValue,
                                    id: snakeCase(singleValue?.inputValue),
                                });
                            } else {
                                // Don't allow removal of fixed single value
                                const currentValue = selectedValues?.[0];
                                if (currentValue?.isFixed && !singleValue) {
                                    // Prevent removal of fixed item
                                    return;
                                }
                                props.onChange(singleValue?.id || null);
                            }
                        }
                    }}
                    filterOptions={(options: NestedSubOption[], params) => {
                        const filtered = filter(options, params);
                        if (!props.allowAdd) return filtered;
                        if (params.inputValue !== '') {
                            filtered.push({
                                inputValue: params.inputValue,
                                // name: `Add "${params.inputValue}"`,
                                name: `Ajouter "${params.inputValue}"`,
                                parent: { name: 'Autre', id: 'other' },
                            });
                        }
                        return filtered;
                    }}
                    options={[...options, ...customOptions]}
                    getOptionLabel={(option: NestedSubOption) => {
                        // e.g. value selected with enter, right from the input
                        if (typeof option === 'string') {
                            return option;
                        }
                        if (option?.inputValue) {
                            return option?.inputValue;
                        }
                        return option?.name;
                    }}
                    fullWidth
                    onOpen={() => {
                        setIsOpen(true);
                    }}
                    onClose={() => {
                        setIsOpen(false);
                    }}
                    disableClearable
                    clearOnBlur
                    handleHomeEndKeys
                    renderOption={(_props, option: NestedSubOption) => {
                        if (props.renderOptionWithIcon) {
                            return props.renderOptionWithIcon(_props, option);
                        }
                        return (
                            <li {..._props}>
                                <span>{option?.name}</span>
                            </li>
                        );
                    }}
                    freeSolo
                    renderTags={(value: NestedSubOption[], getTagProps) =>
                        value.map((option: NestedSubOption, index: number) => {
                            const { key, onDelete, ...tagProps } = getTagProps({ index });
                            return (
                                <TagContainer key={key}>
                                    {option?.isFixed && (
                                        <FixedItemIcon>
                                            <i className="fi-sr-star" />
                                        </FixedItemIcon>
                                    )}
                                    <StyledMuiChip
                                        {...tagProps}
                                        avatar={
                                            option?.icon ? (
                                                <i className={`fi fi-br-${props.iconMapper ? props.iconMapper(option.icon) : option.icon}`} style={{ fontSize: 16 }} />
                                            ) : undefined
                                        }
                                        label={option?.name}
                                        onDelete={option?.isFixed ? undefined : onDelete}
                                        deleteIcon={
                                            option?.isFixed ? undefined : (
                                                <i className="fi fi-br-cross-small" />
                                            )
                                        }
                                        sx={{
                                            paddingLeft: option?.isFixed ? '12px' : '0px',
                                        }}
                                    />
                                </TagContainer>
                            );
                        })
                    }
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder={props.placeholder}
                            onChange={event => {
                                setSearch(event.target.value);
                            }}
                            variant="outlined"
                            sx={{
                                '& .MuiInputBase-root': {
                                    rowGap: '8px',
                                    paddingRight: '80px !important',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: props.error ? 'error.main' : 'inherit',
                                    opacity: 1,
                                },
                            }}
                            size="small"
                            inputRef={ref}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <EndAdornmentContainer
                                        flexDirection={'row'}
                                        alignItems={'center'}>
                                        {!isEmpty(props.value) && (
                                            // Show clear button only if there are non-fixed values to clear
                                            (props.multiple !== false
                                                ? selectedValues?.some(item => !item?.isFixed)
                                                : !selectedValues?.[0]?.isFixed
                                            )
                                        ) && (
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        marginRight: 0.5,
                                                    }}
                                                    onClick={() => {
                                                        if (props.multiple !== false) {
                                                            // For multiple values, keep only fixed items
                                                            const fixedItems = selectedValues?.filter(item => item?.isFixed) || [];
                                                            props.onChange(fixedItems.map(item => item?.id).filter(Boolean));
                                                        } else {
                                                            // For single value, only clear if not fixed
                                                            const currentValue = selectedValues?.[0];
                                                            if (!currentValue?.isFixed) {
                                                                props.onChange(null);
                                                            }
                                                        }
                                                    }}>
                                                    <i
                                                        className="fi fi-br-cross-small"
                                                        style={{ 
                                                            fontSize: 18, 
                                                        }}
                                                    />
                                                </IconButton>
                                            )}
                                        {loading ? (
                                            <CircularProgress 
                                                color="inherit" 
                                                size={16} 
                                                sx={{ marginRight: 2 }} 
                                            />
                                        ) : isOpen ? (
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                }}
                                                onClick={() => {
                                                    setIsOpen(false);
                                                }}>
                                                <i
                                                    className="fi-br-check"
                                                    style={{ 
                                                        fontSize: 14, 
                                                        color: theme.palette.primary.main 
                                                    }}
                                                />
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                }}
                                                onClick={() => {
                                                    setIsOpen(true);
                                                }}>
                                                <i
                                                    className="fi-br-caret-down"
                                                    style={{ 
                                                        fontSize: 16, 
                                                    }}
                                                />
                                            </IconButton>
                                        )}
                                    </EndAdornmentContainer>
                                ),
                            }}
                        />
                    )}
                />
            )}
        </React.Fragment>
    );
});

AutoCompleteField.displayName = 'AutoCompleteField';
